/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  DatiSessioneUtente,
  JwtDto,
  LoginResponse,
  RefreshTokenRequest,
  ReloadRequest,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * @summary Ricarica i dati di sessione utente senza scollegare l'utente.
 */
export const putJwtReload = (
  reloadRequest: ReloadRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<JwtDto>(
    {
      url: `/procedimenti-authentication-manager/jwt/reload`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: reloadRequest,
    },
    options,
  );
};

export const getPutJwtReloadMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putJwtReload>>,
    TError,
    { data: ReloadRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putJwtReload>>,
  TError,
  { data: ReloadRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putJwtReload>>,
    { data: ReloadRequest }
  > = props => {
    const { data } = props ?? {};

    return putJwtReload(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutJwtReloadMutationResult = NonNullable<
  Awaited<ReturnType<typeof putJwtReload>>
>;
export type PutJwtReloadMutationBody = ReloadRequest;
export type PutJwtReloadMutationError = ErrorType<unknown>;

/**
 * @summary Ricarica i dati di sessione utente senza scollegare l'utente.
 */
export const usePutJwtReload = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putJwtReload>>,
    TError,
    { data: ReloadRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutJwtReloadMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Aggiorna l'access token
 */
export const postJwtRefreshToken = (
  refreshTokenRequest: RefreshTokenRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<JwtDto>(
    {
      url: `/procedimenti-authentication-manager/jwt/refresh-token`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: refreshTokenRequest,
    },
    options,
  );
};

export const getPostJwtRefreshTokenMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtRefreshToken>>,
    TError,
    { data: RefreshTokenRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJwtRefreshToken>>,
  TError,
  { data: RefreshTokenRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJwtRefreshToken>>,
    { data: RefreshTokenRequest }
  > = props => {
    const { data } = props ?? {};

    return postJwtRefreshToken(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJwtRefreshTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJwtRefreshToken>>
>;
export type PostJwtRefreshTokenMutationBody = RefreshTokenRequest;
export type PostJwtRefreshTokenMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna l'access token
 */
export const usePostJwtRefreshToken = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtRefreshToken>>,
    TError,
    { data: RefreshTokenRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostJwtRefreshTokenMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * durata token 10 minuti, refresh token 120 minuti
 * @summary Effettua il login mock con codicefiscale ed ottiene i token JWT (access & refresh) in cambio
 */
export const postJwtLogin = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<LoginResponse>(
    { url: `/procedimenti-authentication-manager/jwt/login`, method: 'post' },
    options,
  );
};

export const getPostJwtLoginMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtLogin>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJwtLogin>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJwtLogin>>,
    TVariables
  > = () => {
    return postJwtLogin(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJwtLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJwtLogin>>
>;

export type PostJwtLoginMutationError = ErrorType<unknown>;

/**
 * @summary Effettua il login mock con codicefiscale ed ottiene i token JWT (access & refresh) in cambio
 */
export const usePostJwtLogin = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtLogin>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostJwtLoginMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * durata token 10 minuti, refresh token 120 minuti
 * @summary Effettua il login con credenziali username e password, ed ottiene i token JWT (access & refresh) in cambio
 */
export const getJwtLoginIdp = (
  idp: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<LoginResponse>(
    {
      url: `/procedimenti-authentication-manager/jwt/login/${idp}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetJwtLoginIdpQueryKey = (idp: string) =>
  [`/procedimenti-authentication-manager/jwt/login/${idp}`] as const;

export const getGetJwtLoginIdpQueryOptions = <
  TData = Awaited<ReturnType<typeof getJwtLoginIdp>>,
  TError = ErrorType<unknown>,
>(
  idp: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getJwtLoginIdp>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getJwtLoginIdp>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetJwtLoginIdpQueryKey(idp);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getJwtLoginIdp>>> = ({
    signal,
  }) => getJwtLoginIdp(idp, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idp, ...queryOptions };
};

export type GetJwtLoginIdpQueryResult = NonNullable<
  Awaited<ReturnType<typeof getJwtLoginIdp>>
>;
export type GetJwtLoginIdpQueryError = ErrorType<unknown>;

/**
 * @summary Effettua il login con credenziali username e password, ed ottiene i token JWT (access & refresh) in cambio
 */
export const useGetJwtLoginIdp = <
  TData = Awaited<ReturnType<typeof getJwtLoginIdp>>,
  TError = ErrorType<unknown>,
>(
  idp: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getJwtLoginIdp>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetJwtLoginIdpQueryOptions(idp, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * durata token 10 minuti, refresh token 120 minuti
 * @summary Effettua il login con credenziali username e password, ed ottiene i token JWT (access & refresh) in cambio
 */
export const postJwtLoginIdp = (
  idp: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<LoginResponse>(
    {
      url: `/procedimenti-authentication-manager/jwt/login/${idp}`,
      method: 'post',
    },
    options,
  );
};

export const getPostJwtLoginIdpMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtLoginIdp>>,
    TError,
    { idp: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJwtLoginIdp>>,
  TError,
  { idp: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJwtLoginIdp>>,
    { idp: string }
  > = props => {
    const { idp } = props ?? {};

    return postJwtLoginIdp(idp, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJwtLoginIdpMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJwtLoginIdp>>
>;

export type PostJwtLoginIdpMutationError = ErrorType<unknown>;

/**
 * @summary Effettua il login con credenziali username e password, ed ottiene i token JWT (access & refresh) in cambio
 */
export const usePostJwtLoginIdp = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJwtLoginIdp>>,
    TError,
    { idp: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostJwtLoginIdpMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Ottiene il profilo dell'utente attualmente loggato, comprensivo di eventuali ulteriori informazioni annesse.
 */
export const getJwtUtente = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<DatiSessioneUtente>(
    {
      url: `/procedimenti-authentication-manager/jwt/utente`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetJwtUtenteQueryKey = () =>
  [`/procedimenti-authentication-manager/jwt/utente`] as const;

export const getGetJwtUtenteQueryOptions = <
  TData = Awaited<ReturnType<typeof getJwtUtente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getJwtUtente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<Awaited<ReturnType<typeof getJwtUtente>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetJwtUtenteQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getJwtUtente>>> = ({
    signal,
  }) => getJwtUtente(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetJwtUtenteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getJwtUtente>>
>;
export type GetJwtUtenteQueryError = ErrorType<unknown>;

/**
 * @summary Ottiene il profilo dell'utente attualmente loggato, comprensivo di eventuali ulteriori informazioni annesse.
 */
export const useGetJwtUtente = <
  TData = Awaited<ReturnType<typeof getJwtUtente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getJwtUtente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetJwtUtenteQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
